<script setup>
import { computed, watch, ref, onUnmounted } from 'vue'
import { useTelephonyOmnichannelStore } from '@/stores/telephonyOmnichannel'
import { useConfigurations } from '@/stores/configurations'
import Button from 'primevue/button'
import photoDefault from '@/assets/images/no_user.jpeg'
import ringAudio from '@/assets/audio/ringphone.mp3'

const store = useTelephonyOmnichannelStore()
const configStore = useConfigurations()

const audio = ref(null)

const isEnabled = computed(() =>
    configStore.business?.chat_telephony_omnichannel_enabled
    || configStore.business?.settings?.chat_telephony_omnichannel_enabled
)

const clientPhoto = computed(() => store.clientPhoto || photoDefault)

function playRing() {
    if (!audio.value) {
        audio.value = new Audio(ringAudio)
        audio.value.loop = true
    }
    audio.value.currentTime = 0
    audio.value.play().catch(() => {})
}

function stopRing() {
    if (audio.value) {
        audio.value.pause()
        audio.value.currentTime = 0
    }
}

watch(() => store.callStatus, (status, oldStatus) => {
    if (status === 'ringing') {
        playRing()
    } else if (oldStatus === 'ringing') {
        stopRing()
    }
})

onUnmounted(() => {
    stopRing()
})

function handleAccept() {
    store.acceptCall()
}

function handleReject() {
    store.rejectCall()
}

function handleEnd() {
    store.endCall()
}
</script>

<template>
    <Transition name="omnichannel-call">
        <div
            v-if="isEnabled && store.hasActiveCall"
            class="fixed bottom-6 left-24 z-50"
        >
            <div
                :class="[
                    'w-[320px] rounded-2xl shadow-2xl border overflow-hidden backdrop-blur-sm transition-all duration-300',
                    store.isRinging
                        ? 'bg-white/95 dark:bg-neutral-800/95 border-green-400 dark:border-green-500 shadow-green-200/50 dark:shadow-green-900/30'
                        : 'bg-white/95 dark:bg-neutral-800/95 border-blue-400 dark:border-blue-500 shadow-blue-200/50 dark:shadow-blue-900/30'
                ]"
            >
                <!-- Status bar -->
                <div
                    :class="[
                        'px-4 py-2 flex items-center gap-2 text-white text-xs font-medium',
                        store.isRinging ? 'bg-green-500' : store.isCalling ? 'bg-yellow-500' : 'bg-blue-500'
                    ]"
                >
                    <i :class="['pi', store.isRinging ? 'pi-phone animate-bounce' : 'pi-phone']" />
                    <span>{{ store.isRinging ? 'Chamada recebida' : store.isCalling ? 'Chamando...' : 'Em chamada' }}</span>
                    <span v-if="store.isInCall" class="ml-auto font-mono">{{ store.callDuration }}</span>
                    <span v-if="store.isRinging" class="ml-auto flex items-center gap-1">
                        <span class="relative flex h-2 w-2">
                            <span class="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
                            <span class="relative inline-flex rounded-full h-2 w-2 bg-white" />
                        </span>
                        Tocando...
                    </span>
                    <span v-if="store.isCalling" class="ml-auto flex items-center gap-1">
                        <i class="pi pi-spin pi-spinner text-xs" />
                    </span>
                </div>

                <!-- Contact info -->
                <div class="px-4 py-3 flex items-center gap-3">
                    <div class="relative shrink-0">
                        <img
                            :src="clientPhoto"
                            :alt="store.clientName"
                            class="w-12 h-12 rounded-full object-cover border-2"
                            :class="store.isRinging ? 'border-green-400' : 'border-blue-400'"
                        >
                        <span
                            v-if="store.isRinging"
                            class="absolute -top-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-neutral-800 animate-pulse"
                        />
                    </div>
                    <div class="flex-1 min-w-0">
                        <p class="text-sm font-semibold text-gray-800 dark:text-gray-100 truncate">
                            {{ store.clientName }}
                        </p>
                        <p class="text-xs text-gray-500 dark:text-gray-400 truncate">
                            {{ store.clientPhone }}
                        </p>
                        <p class="text-[11px] text-gray-400 dark:text-gray-500 truncate mt-0.5">
                            <i class="pi pi-hashtag text-[9px]" />
                            {{ store.channelName }}
                        </p>
                    </div>
                </div>

                <!-- Actions -->
                <div class="px-4 pb-4 flex gap-2">
                    <template v-if="store.isRinging">
                        <Button
                            icon="pi pi-phone"
                            label="Atender"
                            severity="success"
                            class="flex-1"
                            @click="handleAccept"
                        />
                        <Button
                            icon="pi pi-times"
                            label="Rejeitar"
                            severity="danger"
                            outlined
                            class="flex-1"
                            @click="handleReject"
                        />
                    </template>

                    <template v-else-if="store.isCalling">
                        <Button
                            icon="pi pi-times"
                            label="Cancelar"
                            severity="danger"
                            outlined
                            class="flex-1"
                            @click="handleEnd"
                        />
                    </template>

                    <template v-else-if="store.isInCall">
                        <Button
                            icon="pi pi-phone"
                            label="Encerrar"
                            severity="danger"
                            class="flex-1"
                            @click="handleEnd"
                        />
                    </template>
                </div>
            </div>
        </div>
    </Transition>
</template>

<style scoped>
.omnichannel-call-enter-active {
    transition: all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
}
.omnichannel-call-leave-active {
    transition: all 0.25s ease-in;
}
.omnichannel-call-enter-from {
    opacity: 0;
    transform: translateY(40px) scale(0.9);
}
.omnichannel-call-leave-to {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
}
</style>
